/**
 * Amplitude enrichment plugin that strips access secrets out of every URL- and
 * query-valued event property — the ones the SDK stamps on, and the ones our
 * own `trackEvent` calls pass in.
 *
 * Two shapes, two allowlists: {@link URL_BEARING_EVENT_PROPERTIES} hold a whole
 * href, {@link QUERY_BEARING_EVENT_PROPERTIES} hold a bare query string. The
 * URL redactor no-ops on a string with no `?`, so the second group needs its own
 * entry point rather than a wider allowlist.
 *
 * **Why a plugin, and not the `trackEvent` wrapper.** The URL properties are
 * added by the SDK's own page-URL enrichment plugin *after* `track()` has built
 * the event, and autocapture events (clicks, page views, form interactions)
 * never pass through `src/hooks/useAmplitudeTracking.ts` at all. Scrubbing the
 * base payload there cannot reach either.
 *
 * **Why registration order matters.** Verified against
 * `@amplitude/analytics-core`'s `Timeline`: enrichment plugins execute in
 * registration order, and `AmplitudeBrowser._init` registers the SDK's own
 * `pageUrlEnrichmentPlugin` LAST among its built-ins, after draining the
 * pre-init `add()` queue. A plugin handed to `amplitude.add()` before `init()`
 * therefore runs BEFORE the enrichment and gets its work overwritten. This one
 * must be registered once `init().promise` has resolved — see
 * `src/lib/amplitude.ts`.
 *
 * @see ./urlSecrets.ts for the redaction itself
 */

import { redactQuerySecrets, redactUrlSecrets } from './urlSecrets';

export const URL_SECRET_REDACTION_PLUGIN_NAME = 'meetball-url-secret-redaction';

/**
 * Event properties that carry a whole URL — or a path WITH its query string —
 * and so can leak a secret.
 *
 * **The inclusion rule is about the VALUE, not about who wrote it.** Any
 * property holding a URL or a path-with-query belongs here, whether the SDK
 * stamps it on or one of our own `trackEvent` calls passes it in. Asking only
 * "what does the SDK add?" is why this allowlist's first pass missed
 * `landing_path`: it is app-authored, so that question never reached it,
 * leaving one of this plugin's own carriers uncovered. When you add a
 * property whose value comes from `location`, an `href`, a router location or
 * `document.referrer`, add it here in the same change — or keep the value
 * query-free (a bare `location.pathname` carries no secret and needs nothing).
 *
 * - `[Amplitude] Page Location` — the page URL, stamped on EVERY event by the
 *   SDK's page-URL enrichment plugin, and set directly by the page-view plugin.
 *   This is the property the containment exists for.
 * - `[Amplitude] Previous Page Location` — the href of the page navigated FROM.
 *   Set unconditionally by the same plugin, so a token survives one navigation
 *   past the page that carried it.
 * - `[Amplitude] Element Href` — the `href` of a clicked anchor, captured by
 *   the autocapture plugin. No anchor renders an invite link today; included so
 *   one added later does not silently reopen the leak.
 * - `page_location` — the lowercase spelling used by the attribution campaign
 *   tracker's `Page View` event.
 * - `landing_path` — OURS, not the SDK's: `AnalyticsBootstrapper` builds it as
 *   `${pathname}${search}${hash}` and attaches it to the three arrival events
 *   (`qr_code_scanned`, `referral_link_clicked`, `shared_link_opened`) — the
 *   exact events an invite link fires. Relative, not absolute, which
 *   `redactUrlSecrets` handles: it splits on `?` and `#`, never parses an origin.
 * - `referrer` (OURS): `kit_viewed` sends `document.referrer`, which on an unlisted
 *   Space keeps `?invite=`.
 *
 * `[Amplitude] Page URL`, `Page Path` and `Page Domain` are deliberately absent:
 * the SDK already builds them query-free. So are our own `path`, `urlPath`,
 * `source_path`, `source_context` and `intended_path`, which are all bare
 * `location.pathname`.
 */
export const URL_BEARING_EVENT_PROPERTIES = [
  '[Amplitude] Page Location',
  '[Amplitude] Previous Page Location',
  '[Amplitude] Element Href',
  'page_location',
  'landing_path',
  'referrer',
] as const;

/**
 * Event properties that carry a BARE query string (`key=value&…`, no leading
 * `?`), which `redactUrlSecrets` would pass through untouched — it looks for a
 * `?` and finds none. These go through `redactQuerySecrets` instead.
 *
 * - `[Amplitude] URL Query` — the query string of a captured API request, from
 *   the network-capture plugin. `autocapture: true` switches that plugin on
 *   (`isNetworkTrackingEnabled` returns the boolean straight back), and with no
 *   `captureRules` it emits `[Amplitude] Network Request` whenever the status is
 *   5xx **or** `undefined` (a failed or aborted request). The invite token is a
 *   query parameter on `GET /api/v1/spaces/{id}?token=…`
 *   (`getCommunityDetail`), so one backend incident or one flaky connection puts
 *   the raw token in this property. Verified against the installed
 *   `@amplitude/plugin-network-capture-browser`, which builds it as
 *   `new URL(url).searchParams.toString()`.
 *
 * `[Amplitude] URL` on the same event needs nothing: the plugin blanks `search`
 * and `hash` before reading it.
 */
export const QUERY_BEARING_EVENT_PROPERTIES = ['[Amplitude] URL Query'] as const;

/**
 * User properties that carry a raw URL. Attribution stores `document.referrer`,
 * which is the full previous URL after a hard navigation away from a
 * secret-bearing page — and `initial_referrer` is a `$setOnce`, so it would
 * pin that secret to the user profile permanently.
 */
export const URL_BEARING_USER_PROPERTIES = ['referrer', 'initial_referrer'] as const;

/** Redacts one property value, chosen by the shape that property holds. */
type Redactor = (value: string) => string;

const buildRedactors = (
  urlKeys: readonly string[],
  queryKeys: readonly string[] = [],
): ReadonlyMap<string, Redactor> =>
  new Map<string, Redactor>([
    ...urlKeys.map(key => [key, redactUrlSecrets] as const),
    ...queryKeys.map(key => [key, redactQuerySecrets] as const),
  ]);

const EVENT_PROPERTY_REDACTORS = buildRedactors(
  URL_BEARING_EVENT_PROPERTIES,
  QUERY_BEARING_EVENT_PROPERTIES,
);
const USER_PROPERTY_REDACTORS = buildRedactors(URL_BEARING_USER_PROPERTIES);

/**
 * User properties arrive wrapped in identify operations (`{ $set: {...},
 * $setOnce: {...} }`), so the walk needs one level of nesting there. Event
 * properties are flat.
 */
const USER_PROPERTY_DEPTH = 1;
const EVENT_PROPERTY_DEPTH = 0;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Redact the allowlisted string values in `source`, recursing `depth` levels
 * into nested objects.
 *
 * Returns `null` when nothing changed, so the caller can keep the original
 * object rather than allocating an equal copy. Only allowlisted keys holding a
 * string that actually contains a secret are ever rewritten — every other
 * property is passed through by reference, which is what keeps the blast radius
 * of this plugin to the secret itself.
 */
const redactPropertyBag = (
  source: Record<string, unknown>,
  redactors: ReadonlyMap<string, Redactor>,
  depth: number,
): Record<string, unknown> | null => {
  let result: Record<string, unknown> | null = null;

  for (const [key, value] of Object.entries(source)) {
    let next: unknown;
    const redact = redactors.get(key);

    if (typeof value === 'string' && redact) {
      next = redact(value);
    } else if (depth > 0 && isPlainObject(value)) {
      next = redactPropertyBag(value, redactors, depth - 1) ?? value;
    } else {
      continue;
    }

    if (next !== value) {
      result = result ?? { ...source };
      result[key] = next;
    }
  }

  return result;
};

/** The subset of an Amplitude event this plugin reads. */
interface RedactableEvent {
  event_properties?: unknown;
  user_properties?: unknown;
}

/**
 * Return `event` with every secret-bearing URL property redacted.
 *
 * Returns the SAME object when there is nothing to redact, so an event that
 * carries no secret is passed through untouched.
 */
export const redactEventUrlSecrets = <T extends RedactableEvent>(event: T): T => {
  const eventProperties = event.event_properties;
  const userProperties = event.user_properties;

  const nextEventProperties = isPlainObject(eventProperties)
    ? redactPropertyBag(eventProperties, EVENT_PROPERTY_REDACTORS, EVENT_PROPERTY_DEPTH)
    : null;
  const nextUserProperties = isPlainObject(userProperties)
    ? redactPropertyBag(userProperties, USER_PROPERTY_REDACTORS, USER_PROPERTY_DEPTH)
    : null;

  if (!nextEventProperties && !nextUserProperties) {
    return event;
  }

  return {
    ...event,
    ...(nextEventProperties ? { event_properties: nextEventProperties } : {}),
    ...(nextUserProperties ? { user_properties: nextUserProperties } : {}),
  };
};

/**
 * Build the enrichment plugin. Must be registered AFTER `init()` resolves —
 * see the module docstring.
 */
export const urlSecretRedactionPlugin = () => ({
  name: URL_SECRET_REDACTION_PLUGIN_NAME,
  type: 'enrichment' as const,
  execute: <T extends RedactableEvent>(event: T): Promise<T> =>
    Promise.resolve(redactEventUrlSecrets(event)),
});
